/**
 * 未匹配费用面板
 *
 * 功能：
 * - 展示未匹配航班的费用项
 * - 这些费用需要重点审核
 */

import React from "react";
import { Alert, Table, Tag, Typography, Tooltip } from "antd";
import { WarningOutlined, LinkOutlined } from "@ant-design/icons";
import { buildCurrencyConversionDisplay } from "./currencyDisplay.js";

const { Text, Link } = Typography;

const SOURCE_META = {
    PENDING_AAI: { color: "blue", text: "待审" },
    COMMITTED_INVOICE: { color: "green", text: "已入库" },
};

/**
 * 格式化金额
 */
const formatAmount = (amount, currency = "USD") => {
    if (amount == null) return "-";
    const num = Number(amount);
    return num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/**
 * 未匹配费用面板
 */
export function UnmatchedChargesPanel({ charges, currency, products, invoiceMap, onInvoiceClick }) {

    if (!charges?.length) return null;

    // 计算总金额
    const totalAmount = charges.reduce((sum, c) => sum + (c.amount || 0), 0);

    // 表格列定义
    const columns = [
        {
            title: "来源",
            dataIndex: "source_type",
            key: "source_type",
            width: 90,
            render: (sourceType) => {
                const sourceMeta = SOURCE_META[sourceType] || { color: "default", text: sourceType || "未知" };
                return <Tag color={sourceMeta.color}>{sourceMeta.text}</Tag>;
            },
        },
        {
            title: "账单",
            dataIndex: "invoice_number",
            key: "invoice_number",
            width: 160,
            ellipsis: true,
            render: (text, record) => {
                // 从统一的 invoiceMap 获取账单标识（Context 层已计算最小区分前缀）
                const invoiceIdentity = record.invoice_data_source_id
                    ? invoiceMap?.[record.invoice_data_source_id]
                    : null;
                const fullNumber = invoiceIdentity?.invoice_number || text;
                const displayLabel = invoiceIdentity?.short_label || fullNumber;

                if (record.invoice_data_source_id) {
                    return (
                        <Tooltip title={fullNumber}>
                            <Link
                                onClick={() => onInvoiceClick?.(record.invoice_data_source_id)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            >
                                <LinkOutlined />
                                {displayLabel}
                            </Link>
                        </Tooltip>
                    );
                }
                return (
                    <Tooltip title={fullNumber}>
                        <Tag>{displayLabel}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: "产品",
            dataIndex: "product_code",
            key: "product_code",
            width: 150,
            render: (code, record) => {
                const productInfo = products?.[code];
                const displayName = productInfo?.name || record.product_name || code;
                return (
                    <Tooltip title={record.description}>
                        <span>{displayName}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: "描述",
            dataIndex: "description",
            key: "description",
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <span style={{ color: "#666" }}>{text || "-"}</span>
                </Tooltip>
            ),
        },
        {
            title: "数量",
            dataIndex: "quantity",
            key: "quantity",
            width: 60,
            align: "right",
            render: (val, record) => {
                const quantity = val || 1;
                return record.unit ? `${quantity} ${record.unit}` : quantity;
            },
        },
        {
            title: "单价",
            dataIndex: "unit_price",
            key: "unit_price",
            width: 100,
            align: "right",
            render: (val) => formatAmount(val, currency),
        },
        {
            title: "金额",
            dataIndex: "amount",
            key: "amount",
            width: 120,
            align: "right",
            render: (val, record) => {
                const baseCurrency = record.base_currency || record.currency || currency;
                const conversionDisplay = buildCurrencyConversionDisplay(record, baseCurrency);
                return (
                    <div style={{ textAlign: "right" }}>
                        <Text strong style={{ color: "#fa8c16" }}>
                            {baseCurrency} {formatAmount(val)}
                        </Text>
                        {conversionDisplay && (
                            <Tooltip
                                title={
                                    conversionDisplay.formula ? (
                                        <div>
                                            <div>{conversionDisplay.formula}</div>
                                            <div style={{ marginTop: 4, color: "#d9d9d9" }}>后端汇率仍按 {conversionDisplay.originalCurrency} → {conversionDisplay.baseCurrency} 计算。</div>
                                        </div>
                                    ) : null
                                }
                            >
                                <div style={{ fontSize: 11, color: "#8c8c8c", cursor: conversionDisplay.formula ? "help" : "default" }}>
                                    <div>{conversionDisplay.originalLabel}</div>
                                    {conversionDisplay.rateLabel && <div>汇率 {conversionDisplay.rateLabel}</div>}
                                </div>
                            </Tooltip>
                        )}
                        {record.conversion_warning && (
                            <Tooltip title={record.conversion_warning.message || record.conversion_warning.code}>
                                <Tag color="warning" style={{ fontSize: 10, margin: 0 }}>折算提示</Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            title: "行号",
            dataIndex: "source_lines",
            key: "source_lines",
            width: 60,
            render: (lines) => {
                if (!lines?.length) return "-";
                return <Tag color="default">{lines.join(",")}</Tag>;
            },
        },
    ];

    // 数据源
    const dataSource = charges.map((charge, idx) => ({
        key: charge.item_id || idx,
        ...charge,
    }));

    return (
        <div className="unmatched-charges-panel" style={{ marginBottom: 16 }}>
            <Alert
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                message={
                    <span>
                        <strong>未匹配航班的费用（{charges.length} 项）</strong>
                        <span style={{ marginLeft: 16 }}>
                            合计: <strong>{currency} {formatAmount(totalAmount)}</strong>
                        </span>
                    </span>
                }
                description="以下费用项未能匹配到具体航班，需要人工审核确认或补充匹配信息。"
                style={{ marginBottom: 12 }}
            />

            <Table
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                size="small"
                scroll={{ x: "max-content" }}
                summary={() => (
                    <Table.Summary fixed>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={6}>
                                <Text strong>合计</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={6} align="right">
                                <Text strong style={{ color: "#fa8c16", fontSize: 14 }}>
                                    {currency} {formatAmount(totalAmount)}
                                </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={7} />
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
            />
        </div>
    );
}

export default UnmatchedChargesPanel;
